import React, { useContext, useEffect, useReducer, useRef, useState } from "react";
import { Data } from "../../Data";
import { toast } from "sonner";
import { getError } from "../../utils/utils";
import Axios from "axios";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { getCountryCodes } from "../utils/LocationUtils";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Alert } from "../../components/ui/alert";
import { Spinner } from "../../components/ui/spinner";
import { Skeleton } from "../../components/ui/skeleton";
import NotificationBanners from "../components/NotificationBanner";
import { useNotifications } from "../utils/useNotifications";
import useBodyLock from "../utils/useBodyLock";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select } from "../../components/ui/select";
import {
    Accordion,
    AccordionItem,
    AccordionTrigger,
    AccordionContent,
} from "../../components/ui/accordion";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogBody,
    DialogCloseButton,
} from "../../components/ui/dialog";

const reducer = (state, action) => {
    switch (action.type) {
        case "FETCH_REQUEST":
            return { ...state, loading: true };
        case "FETCH_SUCCESS":
            return { ...state, infoData: action.payload, loading: false };
        case "FETCH_FAIL":
            return { ...state, loading: false };
        case "FETCH_REQUEST_AUTH":
            return { ...state, loadingAuth: true };
        case "FETCH_SUCCESS_AUTH":
            return { ...state, loadingAuth: false };
        case "FETCH_FAIL_AUTH":
            return { ...state, loadingAuth: false };
        case "FETCH_REQUEST_LOGIN":
            return { ...state, loadingLogin: true };
        case "FETCH_SUCCESS_LOGIN":
            return { ...state, loadingLogin: false };
        case "FETCH_FAIL_LOGIN":
            return { ...state, loadingLogin: false };
        default:
            return state;
    }
};

const EditIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
        <path strokeLinecap="round" strokeLinejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10" />
    </svg>
);

const CloseIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
    </svg>
);

const LockIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
    </svg>
);

const InfoIcon = ({ size = "md" }) => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={1.5}
        stroke="currentColor"
        className={size === "sm" ? "w-3.5 h-3.5 shrink-0" : "w-5 h-5 shrink-0"}
    >
        <path strokeLinecap="round" strokeLinejoin="round" d="m11.25 11.25.041-.02a.75.75 0 0 1 1.063.852l-.708 2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9-3.75h.008v.008H12V8.25Z" />
    </svg>
);

function Tooltip({ text, children }) {
    const anchorRef = useRef(null);
    const [visible, setVisible] = useState(false);
    const [style, setStyle] = useState({ position: "fixed", visibility: "hidden" });
    const TOOLTIP_WIDTH = 224;
    const GAP = 8;

    const computePosition = () => {
        if (!anchorRef.current) return;
        const rect = anchorRef.current.getBoundingClientRect();
        const vw = window.innerWidth;

        let left = rect.left + rect.width / 2 - TOOLTIP_WIDTH / 2;
        if (left + TOOLTIP_WIDTH + GAP > vw) left = vw - TOOLTIP_WIDTH - GAP;
        if (left < GAP) left = GAP;

        const anchorCenter = rect.left + rect.width / 2;
        const caretLeft = Math.max(12, Math.min(anchorCenter - left - 4, TOOLTIP_WIDTH - 20));

        setStyle({
            position: "fixed",
            top: rect.top - GAP,
            left,
            transform: "translateY(-100%)",
            width: TOOLTIP_WIDTH,
        });
        setCaretLeft(caretLeft);
    };

    const [caretLeft, setCaretLeft] = useState(12);

    const show = () => { computePosition(); setVisible(true); };
    const hide = () => setVisible(false);

    return (
        <span
            ref={anchorRef}
            onMouseEnter={show}
            onMouseLeave={hide}
            onFocus={show}
            onBlur={hide}
            className="inline-flex items-center cursor-help"
        >
            {children}
            <span
                role="tooltip"
                style={style}
                className={`pointer-events-none rounded-lg bg-gray-900 px-3 py-2 text-xs text-white shadow-lg transition-opacity z-[9999] ${visible ? "opacity-100" : "opacity-0"}`}
            >
                {text}
                <span
                    style={{ left: caretLeft }}
                    className="absolute top-full border-4 border-transparent border-t-gray-900"
                    aria-hidden="true"
                />
            </span>
        </span>
    );
}

function ReadonlyField({ label, value }) {
    return (
        <AccordionItem value={label} className="pointer-events-none border border-gray-200 rounded-lg overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 gap-3">
                <p className="m-0 text-sm">
                    <strong>{label}:</strong>{" "}{value}
                </p>
                <span className="inline-flex flex-shrink-0 items-center gap-1.5 px-3 py-1.5 bg-red-50 text-red-600 border border-red-200 rounded-md text-xs font-medium">
                    <LockIcon />
                    Nelze upravit
                </span>
            </div>
        </AccordionItem>
    );
}

export default function Profile() {
    const [{ loading, loadingAuth, loadingLogin, infoData }, dispatch] = useReducer(reducer, {
        infoData: [],
        loading: true,
        loadingAuth: false,
        loadingLogin: false,
    });

    const { state } = useContext(Data);
    const { userInfo } = state;
    const { notifications, executeAction, dismiss } = useNotifications();
    useBodyLock(loadingAuth);
    const [showAuth, setShowAuth] = useState(false);
    const [authCode, setAuthCode] = useState("");
    const [formType, setFormType] = useState("");
    const [newEmail, setNewEmail] = useState("");
    const [newPhone, setNewPhone] = useState("");
    const [newPhoneCode, setNewPhoneCode] = useState("+420");

    const handleSaveForm = async (e, form_type) => {
        e.preventDefault();
        if (
            (form_type === "email" && newEmail.length === 0) ||
            (form_type === "phone" && newPhone.length === 0)
        ) {
            toast.error("Pole nesmí být prázdné!");
            return;
        }
        dispatch({ type: "FETCH_REQUEST_AUTH" });
        setFormType(form_type);
        try {
            let dataIn;
            if (form_type === "email") {
                dataIn = { type: form_type, email: newEmail };
            } else if (form_type === "phone") {
                dataIn = { type: form_type, phone: newPhone, phone_code: newPhoneCode };
            }
            const { data } = await Axios.post("/api/profile/request", dataIn, {
                headers: { authorization: `Bearer ${userInfo.token}` },
            });
            dispatch({ type: "FETCH_SUCCESS_AUTH" });
            toast.info(data.message);
            setShowAuth(true);
        } catch (err) {
            dispatch({ type: "FETCH_FAIL_AUTH" });
            toast.error(getError(err));
            setAuthCode("");
        }
    };

    const handleCloseAuth = () => setShowAuth(false);

    const authLoginHandler = async (e) => {
        e.preventDefault();
        if (
            (formType === "email" && newEmail.length === 0) ||
            (formType === "phone" && newPhone.length === 0)
        ) {
            toast.error("Pole nesmí být prázdné!");
            return;
        }
        dispatch({ type: "FETCH_REQUEST_LOGIN" });
        try {
            let dataIn;
            if (formType === "email") {
                dataIn = { type: formType, code: authCode, email: newEmail };
            } else if (formType === "phone") {
                dataIn = { type: formType, code: authCode, phone: newPhone, phone_code: newPhoneCode };
            }
            const { data } = await Axios.post("/api/profile/verify", dataIn, {
                headers: { authorization: `Bearer ${userInfo.token}` },
            });
            dispatch({ type: "FETCH_SUCCESS_LOGIN" });
            toast.success(data.message);
            toast.warning("Údaje mohou být do systému zapsány až za několik minut.");
            setShowAuth(false);
            setNewEmail("");
            setNewPhone("");
            setNewPhoneCode("+420");
            setAuthCode("");
            const fetchData = async () => {
                dispatch({ type: "FETCH_REQUEST" });
                try {
                    const { data } = await Axios.post(
                        "/api/info/",
                        { request: "info" },
                        { headers: { authorization: `Bearer ${userInfo.token}` } }
                    );
                    dispatch({ type: "FETCH_SUCCESS", payload: data });
                } catch (err) {
                    dispatch({ type: "FETCH_FAIL" });
                    toast.error(getError(err));
                }
            };
            fetchData();
        } catch (err) {
            dispatch({ type: "FETCH_FAIL_LOGIN" });
            toast.error(getError(err));
            setAuthCode("");
        }
    };

    useEffect(() => {
        const fetchData = async () => {
            dispatch({ type: "FETCH_REQUEST" });
            try {
                const { data } = await Axios.post(
                    "/api/info/",
                    { request: "info" },
                    { headers: { authorization: `Bearer ${userInfo.token}` } }
                );
                dispatch({ type: "FETCH_SUCCESS", payload: data });
            } catch (err) {
                dispatch({ type: "FETCH_FAIL" });
                toast.error(getError(err));
            }
        };
        fetchData();
    }, [userInfo]);

    return (
        <>
            <Helmet>
                <title>Údaje | De Vries Group, a.s.</title>
            </Helmet>
            {loadingAuth && (
                <div className="loading-box-global">
                    <Spinner size="xl" />
                    <h1 className="text-2xl font-medium">Načítání, prosím vyčkejte...</h1>
                </div>
            )}

            <Dialog open={showAuth} onOpenChange={(open) => { if (!open) handleCloseAuth(); }}>
                <DialogContent size="md" className="relative">
                    <DialogCloseButton />
                    <DialogHeader>
                        <DialogTitle>OVĚŘENÍ</DialogTitle>
                    </DialogHeader>
                    <DialogBody>
                        <h3 className="text-lg font-medium mb-4">Zadejte přístupový kód</h3>
                        <form onSubmit={authLoginHandler}>
                            <Input
                                placeholder="Přístupový kód"
                                value={authCode}
                                minLength={6}
                                maxLength={6}
                                inputMode="numeric"
                                pattern="[0-9]*"
                                autoComplete="one-time-code"
                                onChange={(e) => setAuthCode(e.target.value)}
                                className="mb-4 text-base"
                            />
                            <Alert variant="warning" className="flex gap-2 items-start mb-4 text-sm">
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 shrink-0 mt-0.5">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
                                </svg>
                                Údaje mohou být do systému zapsány až za několik minut od úspěšného uložení.
                            </Alert>
                            <div className="flex items-center gap-3">
                                <Button type="submit">Odeslat</Button>
                                {loadingLogin && <Spinner />}
                            </div>
                        </form>
                    </DialogBody>
                </DialogContent>
            </Dialog>

            <main className="py-8 px-6 max-w-3xl mx-auto">
                <div className="mb-6">
                    <h1 className="text-2xl font-semibold text-foreground">Kontaktní údaje</h1>
                    <p className="text-sm text-muted-foreground mt-0.5">Vaše osobní a kontaktní informace</p>
                </div>

                <NotificationBanners
                    page="profile"
                    notifications={notifications}
                    executeAction={executeAction}
                    dismiss={dismiss}
                />

                {loading ? (
                    <div className="space-y-2">
                        {[
                            { label: 60, badge: 80 },
                            { label: 72, badge: 80 },
                            { label: 80, badge: 80 },
                            { label: 52, badge: 56 },
                            { label: 44, badge: 56 },
                        ].map((row, i) => (
                            <div key={i} className="border border-gray-200 rounded-lg px-4 py-3 flex items-center justify-between gap-3">
                                <Skeleton className={`h-4`} style={{ width: `${row.label * 4}px` }} />
                                <Skeleton className="h-7 rounded-md" style={{ width: `${row.badge * 4}px` }} />
                            </div>
                        ))}
                        <div className="mt-6 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 flex gap-2 items-start">
                            <Skeleton className="h-5 w-5 rounded-full shrink-0 mt-0.5" />
                            <div className="flex-1 space-y-2">
                                <Skeleton className="h-3 w-full" />
                                <Skeleton className="h-3 w-3/4" />
                            </div>
                        </div>
                    </div>
                ) : (
                    <>
                        <Accordion type="multiple" className="space-y-2">
                            <ReadonlyField label="Jméno a příjmení" value={infoData.name} />
                            <ReadonlyField label="Adresa hlavní" value={infoData.main_address} />
                            <ReadonlyField
                                label="Adresa doručovací"
                                value={
                                    infoData.contact_address === "" ? (
                                        <Badge variant="danger">Není vyplněno</Badge>
                                    ) : (
                                        infoData.contact_address
                                    )
                                }
                            />

                            <AccordionItem value="email" className="border border-gray-200 rounded-lg overflow-hidden group/email">
                                <AccordionTrigger hideChevron className="px-4 py-3">
                                    <div className="flex items-center justify-between w-full gap-3 pr-0">
                                        <p className="m-0 text-sm">
                                            <strong>Email:</strong>{" "}{infoData.email}
                                        </p>
                                        <span className="inline-flex flex-shrink-0 items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors">
                                            <span className="group-data-[state=open]/email:hidden inline-flex items-center gap-1.5">
                                                <EditIcon />
                                                Upravit
                                            </span>
                                            <span className="group-data-[state=closed]/email:hidden inline-flex items-center gap-1.5">
                                                <CloseIcon />
                                                Zrušit
                                            </span>
                                        </span>
                                    </div>
                                </AccordionTrigger>
                                <AccordionContent>
                                    <form onSubmit={(e) => handleSaveForm(e, "email")}>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                                            <div>
                                                <Label htmlFor="email" className="mb-1 block">E-mail:</Label>
                                                <Input
                                                    type="email"
                                                    id="email"
                                                    placeholder="Zadejte nový e-mail"
                                                    value={newEmail}
                                                    onChange={(e) => setNewEmail(e.target.value)}
                                                    required
                                                />
                                            </div>
                                        </div>
                                        <Button type="submit" size="sm">Uložit</Button>
                                    </form>
                                </AccordionContent>
                            </AccordionItem>

                            <AccordionItem value="phone" className="border border-gray-200 rounded-lg overflow-hidden group/phone">
                                <AccordionTrigger hideChevron className="px-4 py-3">
                                    <div className="flex items-center justify-between w-full gap-3 pr-0">
                                        <p className="m-0 text-sm">
                                            <strong>Telefon:</strong>{" "}{infoData.phone}
                                        </p>
                                        <span className="inline-flex flex-shrink-0 items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors">
                                            <span className="group-data-[state=open]/phone:hidden inline-flex items-center gap-1.5">
                                                <EditIcon />
                                                Upravit
                                            </span>
                                            <span className="group-data-[state=closed]/phone:hidden inline-flex items-center gap-1.5">
                                                <CloseIcon />
                                                Zrušit
                                            </span>
                                        </span>
                                    </div>
                                </AccordionTrigger>
                                <AccordionContent>
                                    <form onSubmit={(e) => handleSaveForm(e, "phone")}>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                                            <div>
                                                <Label className="mb-1 block text-xs">Předvolba</Label>
                                                <Select
                                                    value={newPhoneCode}
                                                    onChange={(e) => setNewPhoneCode(e.target.value)}
                                                    required
                                                >
                                                    {getCountryCodes.map((country) => (
                                                        <option key={country.code} value={country.dial_code}>
                                                            {country.emoji} {country.dial_code}
                                                        </option>
                                                    ))}
                                                </Select>
                                                <div className="mt-2">
                                                    <Label className="mb-1 flex items-center gap-1 text-xs">
                                                        Telefonní číslo
                                                        <Tooltip text="Zadejte telefonní číslo bez předvolby, bez mezer, bez znaků. Např. 123456789.">
                                                            <InfoIcon size="sm" />
                                                        </Tooltip>
                                                    </Label>
                                                    <Input
                                                        type="text"
                                                        placeholder="Zadejte nové telefonní číslo"
                                                        required
                                                        value={newPhone}
                                                        onChange={(e) => setNewPhone(e.target.value)}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                        <Button type="submit" size="sm">Uložit</Button>
                                    </form>
                                </AccordionContent>
                            </AccordionItem>
                        </Accordion>

                        <Alert variant="info" className="mt-6 flex gap-2 items-start">
                            <InfoIcon />
                            <span className="text-sm">
                                Pokud nesedí jméno, příjmení nebo některá z adres, prosím kontaktujte nás
                                prostřednictvím formuláře na podstránce{" "}
                                <Link to="/requests" className="underline font-medium">
                                    'Žádosti'
                                </Link>
                                .
                            </span>
                        </Alert>
                    </>
                )}
            </main>
        </>
    );
}
